// eslint-disable-next-line no-restricted-imports
import { Grid, UnstyledButton, useMantineTheme, Text } from '@mantine/core';
import { Fragment, useState } from 'react';
import type { SlateLabelEnum, ContestSettings } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';

import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';
import dt from '~/testing';

import {
  SectionGrid,
  SectionGridCol,
  SectionGridLabel,
  SectionGridValue,
  SectionNote,
} from '../components';

import useSegmentedTabs from './useSegmentedTabs';
import ScheduleModal from './ScheduleModal';
import ScheduleTabsControl from './ScheduleTabsControl';
import useGetActionLabel from './useGetActionLabel';
import useGetSegmentedData from './useSegmentedData';

type ScheduleSectionProps = {
  slates: ContestSlate[];
  isSidePanel?: boolean;
  segments?: ContestSettings['segments'];
  slateLabel?: SlateLabelEnum;
  isEntryDeadlinePending?: boolean;
  groupStageSlateId?: string;
};

function ScheduleSection({
  slates = [],
  slateLabel,
  isSidePanel,
  segments,
  isEntryDeadlinePending,
  groupStageSlateId,
}: ScheduleSectionProps) {
  const { t } = useTranslation('contest');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const theme = useMantineTheme();

  const { activeTab, tabs, setActiveTab } = useSegmentedTabs({
    slateLabel,
    slates,
    overallLength: segments.data.length,
  });

  const actionLabel = useGetActionLabel({
    slateLabel,
    activeTab,
    segmentType: segments?.segmentType,
  });

  const items = useGetSegmentedData({
    slates,
    activeTab,
    segments,
    slateLabel,
    groupStageSlateId,
  });

  return (
    <>
      <ScheduleTabsControl
        segments={segments}
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <div>
        {items.slice(0, 10).map((data) => (
          <Fragment key={data.value}>
            <SectionGrid data-test-id={dt.contestDetail.section.prizes.row}>
              <SectionGridCol span={6}>
                <Grid align="center">
                  <SectionGridLabel isSidePanel={isSidePanel} ml={theme.spacing.xl}>
                    {data.label}
                  </SectionGridLabel>
                </Grid>
              </SectionGridCol>
              <SectionGridCol span={6}>
                <SectionGridValue isSidePanel={isSidePanel}>
                  {isEntryDeadlinePending && slates.length === 1
                    ? t('detail.firstTeeTimeTbd')
                    : t('detail.schedule.table.date', { value: data.startDate })}
                </SectionGridValue>
              </SectionGridCol>
            </SectionGrid>
          </Fragment>
        ))}
      </div>
      <SectionNote mt="md">
        {slates.length > 0 && (
          <UnstyledButton onClick={() => setIsModalOpen(true)}>
            <Text color="cyan.5" size="sm">
              {actionLabel}
            </Text>
          </UnstyledButton>
        )}
        <ScheduleModal
          isSidePanel={isSidePanel}
          isOpen={isModalOpen}
          slates={slates}
          segments={segments}
          onClose={() => setIsModalOpen(false)}
          slateLabel={slateLabel}
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          groupStageSlateId={groupStageSlateId}
        />
      </SectionNote>
    </>
  );
}

export default ScheduleSection;
